/**
 * MODULE: domain/pages/frontend/hooks/usePagesCapability.ts
 * PURPOSE: Check whether capability.pages is enabled for the current tenant
 */

import { useQuery } from '@tanstack/react-query';
import { API_CONFIG, getAuthHeaders } from '@shared/lib/frontend/api/config';
import { useSessionAuthReady } from '@shared/lib/frontend/api/sessionAuthReady';

export const PAGES_CAPABILITY_KEY = 'capability.pages';

type CapabilityCheckResponse = {
  capability_key: string;
  enabled: boolean;
  tenant_id?: string;
};

async function fetchPagesCapability(): Promise<CapabilityCheckResponse> {
  const url = `${API_CONFIG.baseURL.replace(/\/$/, '')}/api/v1/features/${PAGES_CAPABILITY_KEY}/enabled`;
  const response = await fetch(url, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    return { capability_key: PAGES_CAPABILITY_KEY, enabled: false };
  }

  return response.json();
}

export function usePagesCapability() {
  const apiReady = useSessionAuthReady();
  return useQuery({
    queryKey: ['capability', PAGES_CAPABILITY_KEY],
    queryFn: fetchPagesCapability,
    enabled: apiReady,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function isPagesCapabilityEnabled(
  data: CapabilityCheckResponse | undefined,
  isLoading: boolean
): boolean {
  if (isLoading) return true;
  return data?.enabled !== false;
}
